import { LibraryAPI } from '@business/api/library';
import {
  CompactLibraryItemResponse,
  FolderId,
  ItemId,
  LibraryFolderBreadcrumbResponse,
  LibraryItemResponse,
  LibraryItemShareItem,
  LibraryItemShareType,
  LibraryItemType,
  LibraryResponse,
  LibraryVersionId,
  LibraryItemStatus,
} from '@business/entities/library';
import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { R } from '@core/utils/r';
import { Repository } from '@core/utils/repository';
import { showDeleteConfirmation } from '@modules/common';
import { BehaviorSubject } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

export class LibraryBloc extends BaseBloc {
  private readonly itemsRepo = new Repository<LibraryItemResponse | LibraryResponse | CompactLibraryItemResponse>({
    getItemId: item => item.id + item.type,
    // getItemId: item => item.id,
  });

  // readonly items$ = this.itemsRepo.items$.pipe(
  //   map(items => {
  //     return R.orderBy(items, ['type', 'name'], ['desc', 'asc']);
  //   }),
  // );

  readonly items$ = this.itemsRepo.items$;

  readonly selectItem = this.itemsRepo.selectItem;
  readonly breadcrumb$ = new BehaviorSubject<LibraryFolderBreadcrumbResponse[]>([]);

  readonly getFolderItems = (folderId: FolderId) => {
    return this.items$.pipe(
      map(items =>
        R.sortBy(
          R.filter(items, i => i.parentFolderId == folderId),
          [item => (item.type === LibraryItemType.folder ? 0 : 1), 'name'],
          ['asc', 'asc'],
        ),
      ),
    );
  };

  readonly folders$ = this.items$.pipe(
    map(items =>
      R.sortBy(
        R.filter(items, i => i.type === 'folder'),
        i => i.name.toLowerCase(),
      ),
    ),
  );

  onReset() {
    this.itemsRepo.reset();
  }

  getItems = (parentId?: FolderId) => {
    return LibraryAPI.getItems(parentId).pipe(
      map(data => {
        this.breadcrumb$.next([...data.breadcrumbs]);
        return data.items;
      }),
      this.itemsRepo.ops.reset(),
      // handleMessage({ error: requestMessage('fetch_files_error') }),
    );
  };

  createFolder = (name: string, parentId?: FolderId) => {
    return LibraryAPI.createFolder(name, parentId).pipe(
      this.itemsRepo.ops.addOne(item => item),
      handleMessage({
        // type: requestMessage('create_folder'),
      }),
    );
  };

  renameFolder = (id: FolderId, name: string) => {
    return LibraryAPI.renameFolder(id, name).pipe(
      this.itemsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        // type: requestMessage('rename_folder'),
      }),
    );
  };

  onDropFolder = (draggedItemId: number, type: LibraryItemType, dropTargetId: ItemId, folderId: FolderId) => {
    return LibraryAPI.onDropFolder(draggedItemId, type, dropTargetId, folderId).pipe(
      map(data => {
        this.breadcrumb$.next([...data.breadcrumbs]);
        return data.items;
      }),
      this.itemsRepo.ops.reset(),
    );
  };

  deleteFolder = (id: FolderId) => {
    return showDeleteConfirmation('Delete Folder', 'Do you really want to remove this folder?').pipe(
      switchMap(() => LibraryAPI.deleteFolder(id)),
      this.itemsRepo.ops.removeOne(() => `folder-${id}`),
      handleMessage({
        type: requestMessage('delete_folder'),
      }),
    );
  };

  getItem = (id: ItemId) => {
    return LibraryAPI.getItem(id).pipe(
      map(data => {
        this.breadcrumb$.next([...data.breadcrumbs]);
        return data.item;
      }),
      this.itemsRepo.ops.upsertOne(item => ({ item })),
      // handleMessage({ error: requestMessage('fetch_library_error') }),
    );
  };

  getItemPublic = (id: ItemId) => {
    return LibraryAPI.getItemPublic(id).pipe(
      map(data => {
        this.breadcrumb$.next([...data.breadcrumbs]);
        return data.item;
      }),
      this.itemsRepo.ops.upsertOne(item => ({ item })),
      // handleMessage({ error: requestMessage('fetch_library_error') }),
    );
  };

  // download = (id: ItemId, filename: string) => {
  //   return LibraryAPI.download(id, filename).pipe(handleMessage({ error: requestMessage('print_file_error') }));
  // };

  createItem = (item: Partial<LibraryItemResponse>, parentId?: FolderId) => {
    return LibraryAPI.createItem(item, parentId).pipe(
      //   this.itemsRepo.ops.addOne(item => item),
      this.itemsRepo.ops.upsertMany(item => ({ item })),
      handleMessage({
        type: requestMessage(
          item.type == LibraryItemType.article
            ? 'create_article_item'
            : // : item.type == LibraryItemType.documentManagement
            // ? 'create_doc_item'
            item.type == LibraryItemType.file
            ? 'create_file_item'
            : item.type == LibraryItemType.folder
            ? 'create_folder_item'
            : item.type == LibraryItemType.policy
            ? 'create_policy_item'
            : item.type == LibraryItemType.procedure
            ? 'create_procedure_item'
            : 'create_item',
        ),
      }),
    );
  };

  duplicateItem = (id: ItemId, title: string) => {
    return LibraryAPI.duplicateItem(id, title).pipe(
      this.itemsRepo.ops.addOne(item => item),
      handleMessage({
        // type: requestMessage('duplicate_library_item'),
      }),
    );
  };

  updateItem = (id: ItemId, item: Partial<LibraryItemResponse>) => {
    return LibraryAPI.updateItem(item, id).pipe(
      this.itemsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage(
          item.type == LibraryItemType.article
            ? 'create_article_item'
            : // : item.type == LibraryItemType.documentManagement
            // ? 'create_doc_item'
            item.type == LibraryItemType.file
            ? 'create_file_item'
            : item.type == LibraryItemType.folder
            ? 'create_folder_item'
            : item.type == LibraryItemType.policy
            ? 'create_policy_item'
            : item.type == LibraryItemType.procedure
            ? 'create_procedure_item'
            : 'create_item',
        ),
      }),
    );
  };

  shareItem = (id: ItemId, items: LibraryItemShareItem[] = [], type: LibraryItemShareType = LibraryItemShareType.item) => {
    return LibraryAPI.shareItem(id, items, type).pipe(
      this.itemsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        // type: requestMessage('update_folder_item'),
      }),
    );
  };

  deleteItem = (id: ItemId) => {
    return showDeleteConfirmation('Delete Item', 'Do you really want to remove this item?').pipe(
      switchMap(() => LibraryAPI.deleteItem(id)),
      this.itemsRepo.ops.removeOne(() => `policy-${id}`),
      handleMessage({
        type: requestMessage('delete_item'),
      }),
    );
  };

  removeFile = (id: ItemId, index: number) => {
    return showDeleteConfirmation('Delete File', 'Do you really want to remove this item?').pipe(switchMap(() => LibraryAPI.removeFile(id, index)));
  };

  removeFileAttachment = (id: ItemId) => {
    return showDeleteConfirmation('Delete File', 'Do you really want to remove this item?').pipe(
      switchMap(() => LibraryAPI.removeFileAttachment(id)),
    );
  };

  search = (search: string, type: LibraryItemType, parentId?: FolderId) => {
    return LibraryAPI.search(search, type, parentId).pipe(
      handleMessage({
        error: requestMessage('fetch_general_error'),
      }),
    );
  };

  updateAssignne = (type: string, id: number | string, items: string[]) => {
    return LibraryAPI.updateAssignne(type, id, items).pipe(this.itemsRepo.ops.upsertOne(item => ({ item })));
  };

  // folderStructure = (id: FolderId) => {
  //   return LibraryAPI.folderStructure(id).pipe(
  //     handleMessage({
  //       error: requestMessage('fetch_general_error'),
  //     }),
  //   );
  // };

  // move = (id: string, type: LibraryItemType, destinationId: string) => {
  //   return LibraryAPI.move(id, type, destinationId).pipe(
  //     handleMessage({
  //       // error: requestMessage('move_folder_error'),
  //     }),
  //   );
  // };

  // renameVersion = (itemId: string, id: LibraryVersionId, title: string) => {
  //   return LibraryAPI.renameVersion(id, title).pipe(
  //     this.itemsRepo.ops.updateOne(versions => ({
  //       id: itemId,
  //       changes: () => ({ versions }),
  //     })),
  //     handleMessage({
  //       // type: requestMessage('rename_revision'),
  //     }),
  //   );
  // };

  // deleteVersion = (itemId: string, id: LibraryVersionId) => {
  //   return showDeleteConfirmation('Delete Revision', 'Do you really want to remove this revision?').pipe(
  //     switchMap(() => LibraryAPI.deleteVersion(id)),
  //     this.itemsRepo.ops.updateOne(versions => ({
  //       id: itemId,
  //       changes: () => ({ versions }),
  //     })),
  //     handleMessage({
  //       // type: requestMessage('delete_revision'),
  //     }),
  //   );
  // };

  // onStatusChange = (id: ItemId, status: LibraryItemStatus) => {
  //   return LibraryAPI.onStatusChange(id, status).pipe(
  //     this.itemsRepo.ops.upsertOne(item => ({ item })),
  //     handleMessage({
  //       // error: requestMessage('move_folder_error'),
  //     }),
  //   );
  // };

  createMedia = (item: Partial<LibraryItemResponse>, parentId?: FolderId) => {
    return LibraryAPI.createMedia(item, parentId).pipe(
      //   this.itemsRepo.ops.addOne(item => item),
      this.itemsRepo.ops.upsertMany(item => ({ item })),
      handleMessage({
        type: requestMessage('create_media_item'),
      }),
    );
  };

  updateMedia = (id: ItemId, item: Partial<LibraryItemResponse>) => {
    return LibraryAPI.updateMedia(item, id).pipe(
      this.itemsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_media_item'),
      }),
    );
  };
}
